import React, { useCallback } from 'react'
import { Text } from 'react-native'
import Layout from '../Layout'
import { observer } from 'mobx-react'
import SubmitButton from '../SubmitButton'
import SignInStore from '../stores/SignInStore'
import RootStore from '../../../store/RootStore'
import Title, { SubTitle } from '../Title'
import { Link } from '../../../ui/atoms/Link'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { Spacer } from '../../../ui/atoms'
import LinkLabel from '../LinkLabel'
import PasswordInput from '../PasswordInput'
import { dispatcher, pathFactory, paths } from '../../links'
import { Trans, useTranslation } from 'react-i18next'
import { ErrorMessage } from '../ErrorMessage'
import BackButton from '../BackButton'
import LocalStorage from '../../../store/LocalStorage'

export default observer(({ navigation, dontNavigate }) => {
  const { login, password, canSubmit, errors, setCanAskPassword, removeError } =
    SignInStore
  const { isBusy } = RootStore
  const { t } = useTranslation(['auth'])

  const pressContinue = useCallback(async () => {
    if (!canSubmit) return
    return SignInStore.signIn()
      .then(() => {
        setCanAskPassword(false)
        if (RootStore.routeParams.loanid) {
          navigation.navigate(paths.Console.Credit.LoanDetails, {
            id: RootStore.routeParams.loanid,
          })
        }
        LocalStorage.get('screen').then((screen) => {
          if (screen) {
            LocalStorage.remove('screen')
            const action = dispatcher(pathFactory(screen.screen), screen.params)
            navigation.replace(action.name, action.params)
          }
        })
      })
      .catch(() => {})
  }, [canSubmit, navigation, setCanAskPassword])

  const onBackPress = () => {
    if (errors.get('login')?.message) {
      removeError('login')
    }
    setCanAskPassword(false)
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.navigate(paths.Auth.SignIn)
    }
  }

  return (
    <Layout onBackPress={onBackPress}>
      <BackButton onPress={onBackPress} />
      <Title>{t('auth:signIn.passwordTitle')}</Title>
      <SubTitle style={{ marginTop: 16 }}>
        <Trans
          t={t}
          i18nKey={'auth:signIn.caption'}
          components={{ styled: <Text style={{ color: '#00A0F3' }} /> }}
          values={{ login }}
        />
      </SubTitle>
      <PasswordInput
        testID="password"
        label={t('auth:signIn.passwordLabel')}
        value={password}
        onChangeText={(text) => SignInStore.setPassword(text)}
        onSubmitEditing={pressContinue}
        newPassword
        showEye
        mandatory
      />
      <Spacer />
      <ErrorMessage error={errors.get('login')?.message} />
      <Spacer height={40} />
      <LinkLabel
        screen={paths.Auth.SignInForgotPassword}
        testID="forgot-password"
        style={{ color: '#668598', fontSize: 14 }}
      >
        <Trans
          t={t}
          i18nKey={'auth:signIn.forgotLink'}
          components={{
            styled: (
              <Text
                style={{ color: '#00A0F3' }}
                testID="forgot_password_link"
              />
            ),
          }}
          values={{ login: 'Click Here' }}
        />
      </LinkLabel>
      <Spacer height={20} />
      <SubmitButton
        onPress={pressContinue}
        disabled={!canSubmit || isBusy}
        loading={isBusy}
      >
        {t('auth:signIn.loginButtonLabel')}
      </SubmitButton>
      <Spacer height={40} />
      <Text
        style={{
          fontSize: 16,
          fontWeight: '500',
          color: commonColors.textLight,
          alignSelf: 'center',
        }}
        testID="link-to-signup"
      >
        {`${t('auth:signIn.signUpQuestion')} `}
        <Link
          textStyle={{ fontSize: 16, fontWeight: '500' }}
          title={t('auth:SignUp')}
          color={commonColors.accentText}
          testID="signup_link"
          onPress={() =>
            dontNavigate
              ? dontNavigate(false)
              : navigation.replace(paths.Auth.SignUpHome)
          }
        />
      </Text>
    </Layout>
  )
})
